import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X, Sparkles, CheckCircle2, Circle, ArrowRight, Zap, Youtube } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/AuthProvider";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getOnboardingProgress,
  completeOnboardingStep
} from "@/integrations/supabase/onboarding";
import { getPlanInfo, type PlanInfo } from "@/integrations/supabase/plan-utils";
import {
  ONBOARDING_STEPS,
  EVOLUTION_ONBOARDING_STEPS,
  STEP_CONFIG,
  type OnboardingStep
} from "@/constants/onboarding";
import { OnboardingUpgradeBlock } from "@/components/OnboardingUpgradeBlock";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function EvolvementCard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { empresa } = useAuth();
  const [isVisible, setIsVisible] = useState(true);
  const [planInfo, setPlanInfo] = useState<PlanInfo | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showUpgradeBlock, setShowUpgradeBlock] = useState(false);
  const [isFinalizingOnboarding, setIsFinalizingOnboarding] = useState(false);
  const [upgradeBlockData, setUpgradeBlockData] = useState<{
    title: string;
    description: string;
    features: string[];
    step: OnboardingStep;
  } | null>(null);

  // Buscar progresso do onboarding
  const { data: onboardingProgress } = useQuery({
    queryKey: ["onboarding-progress", empresa?.id],
    queryFn: () => getOnboardingProgress(empresa?.id!),
    enabled: !!empresa?.id,
  });

  // Buscar informações do plano
  useEffect(() => {
    if (empresa?.id) {
      getPlanInfo(empresa.id).then(setPlanInfo);
    }
  }, [empresa?.id]);

  // Auto-marcar etapas baseado em dados
  useEffect(() => {
    if (!empresa?.id || !onboardingProgress?.steps) return;

    const detectAndMarkSteps = async () => {
      try {
        const alreadyCompleted = onboardingProgress.steps.map(s => s.step);

        // Etapa 1: Primeiro produto cadastrado
        if (!alreadyCompleted.includes(ONBOARDING_STEPS.FIRST_PRODUCTS)) {
          const { data: products } = await supabase
            .from("atelie_products")
            .select("id")
            .eq("empresa_id", empresa.id)
            .limit(1);

          if (products && products.length > 0) {
            await completeOnboardingStep(empresa.id, ONBOARDING_STEPS.FIRST_PRODUCTS);
          }
        }

        // Etapa 2: Primeiro serviço cadastrado
        if (!alreadyCompleted.includes(ONBOARDING_STEPS.FIRST_SERVICES)) {
          const { data: services } = await supabase
            .from("atelie_servicos")
            .select("id")
            .eq("empresa_id", empresa.id)
            .limit(1);

          if (services && services.length > 0) {
            await completeOnboardingStep(empresa.id, ONBOARDING_STEPS.FIRST_SERVICES);
          }
        }

        // Etapa 3: Primeiro pedido criado
        if (!alreadyCompleted.includes(ONBOARDING_STEPS.CREATE_ORDER)) {
          const { data: orders } = await supabase
            .from("atelie_orders")
            .select("id")
            .eq("empresa_id", empresa.id)
            .limit(1);

          if (orders && orders.length > 0) {
            await completeOnboardingStep(empresa.id, ONBOARDING_STEPS.CREATE_ORDER);
          }
        }

        // Invalidar query para atualizar UI
        queryClient.invalidateQueries({
          queryKey: ['onboarding-progress', empresa.id]
        });
      } catch (error) {
        console.error("Erro ao detectar etapas:", error);
      }
    };

    detectAndMarkSteps();
  }, [empresa?.id, onboardingProgress?.steps]);

  if (!isVisible || !onboardingProgress?.shouldShowEvolvementCard) {
    return null;
  }

  const steps = EVOLUTION_ONBOARDING_STEPS.map(step => ({
    id: step,
    title: STEP_CONFIG[step].title,
    description: STEP_CONFIG[step].description,
    route: STEP_CONFIG[step].route,
    isOptional: STEP_CONFIG[step].isOptional ?? false,
    requiresPlan: STEP_CONFIG[step].requiresPlan,
    completed: onboardingProgress.steps?.some(s => s.step === step && s.completed) || false,
  }));

  const completed = steps.filter(s => s.completed).length;
  const progress = (completed / steps.length) * 100;

  const handleStepClick = (step: typeof steps[0]) => {
    // Verificar se requer plan superior
    if (step.requiresPlan === "professional" && (!planInfo || !planInfo.isPremium)) {
      setShowUpgradeBlock(true);
      setUpgradeBlockData({
        title: step.title,
        description: step.description,
        features: [
          "Emissão de notas fiscais",
          "Gestão de até 3 operadores",
          "Relatórios avançados",
          "Suporte prioritário"
        ],
        step: step.id,
      });
      return;
    }

    // Navegar para página da etapa ou fechar card se já estiver na página
    if (step.route) {
      const currentPath = window.location.pathname;
      if (currentPath.includes(step.route.split('/')[1])) {
        // Se já está na mesma página, fechar o card do onboarding
        setIsVisible(false);
        toast.success(`${step.title} - você já está na página! 🎯`);
      } else {
        // Senão, navegar para a página
        navigate(step.route);
      }
    }
  };

  const handleSkipStep = async (step: typeof steps[0]) => {
    if (!empresa?.id) return;

    try {
      await completeOnboardingStep(empresa.id, step.id);
      queryClient.invalidateQueries({
        queryKey: ['onboarding-progress', empresa.id]
      });
      toast.success(`✅ Etapa "${step.title}" pulada!`);
    } catch (error) {
      console.error("Erro ao pular etapa:", error);
      toast.error("Erro ao pular etapa");
    }
  };

  const handleFinalizeTour = async () => {
    if (!empresa?.id) return;

    try {
      setIsFinalizingOnboarding(true);
      console.log("🏁 Finalizando onboarding de evolução...");

      // Marcar onboarding como completo
      const { error } = await supabase
        .from('empresas')
        .update({
          onboarding_evolution_completed: true,
          onboarding_evolution_completed_at: new Date().toISOString()
        })
        .eq('id', empresa.id);

      if (error) throw error;

      // Invalidar cache
      queryClient.invalidateQueries({
        queryKey: ['onboarding-progress', empresa.id]
      });

      toast.success("🎉 Onboarding finalizado! Aproveite o Ateliê Pro ao máximo!");
      setIsVisible(false);
    } catch (error) {
      console.error("Erro ao finalizar onboarding:", error);
      toast.error("Erro ao finalizar onboarding");
    } finally {
      setIsFinalizingOnboarding(false);
    }
  };

  if (showUpgradeBlock && upgradeBlockData) {
    return (
      <OnboardingUpgradeBlock
        title={upgradeBlockData.title}
        description={upgradeBlockData.description}
        features={upgradeBlockData.features}
        onUpgrade={() => navigate("/assinatura")}
        onReturnHome={() => window.location.href = "https://app.ateliepro.online/"}
        onSkip={() => {
          handleSkipStep(steps.find(s => s.id === upgradeBlockData.step)!);
          setShowUpgradeBlock(false);
        }}
      />
    );
  }


  return (
    <Card className="mb-6 border-2 border-gradient-to-r from-blue-200 to-purple-200 bg-gradient-to-br from-blue-50 to-purple-50 shadow-xl">
      {/* Banner de parabéns quando completa os básicos */}
      {completed === 0 && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-b-2 border-green-300 p-4">
          <div className="flex items-center gap-3">
            <div className="text-3xl">🎉</div>
            <div>
              <h3 className="font-bold text-green-900">Parabéns!</h3>
              <p className="text-sm text-green-700">Você completou todas as 5 etapas básicas! Agora evolua seu ateliê.</p>
            </div>
          </div>
        </div>
      )}

      <CardHeader className="pb-3">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-blue-600" />
            <CardTitle className="text-xl font-bold text-gray-900">
              🚀 Evolua seu Ateliê
            </CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsVisible(false)}
            className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Barra de progresso */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Progresso</span>
            <span className="text-sm font-semibold text-blue-600">
              {completed}/{steps.length}
            </span>
          </div>
          <div className="w-full bg-gray-300 rounded-full h-3">
            <div
              className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 text-right">{Math.round(progress)}% completo</p>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Lista de etapas */}
        <div className="space-y-3">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className={`p-4 rounded-lg border-2 transition-all ${
                step.completed
                  ? "bg-green-50 border-green-200"
                  : "bg-white border-gray-200 hover:border-blue-300"
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  {step.completed ? (
                    <CheckCircle2 className="h-6 w-6 text-green-600 animate-pulse" />
                  ) : (
                    <Circle className="h-6 w-6 text-gray-400" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h4 className="font-medium text-sm text-gray-900">
                        {index + 1}. {step.title}
                        {step.isOptional && (
                          <span className="ml-2 text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">
                            Opcional
                          </span>
                        )}
                        {step.requiresPlan === "professional" && (!planInfo || !planInfo.isPremium) && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            Plano Profissional
                          </span>
                        )}
                      </h4>
                      <p className="text-xs text-gray-600 mt-1">{step.description}</p>
                    </div>
                  </div>
                </div>

                {!step.completed && (
                  <div className="flex gap-2 flex-shrink-0">
                    {step.isOptional && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSkipStep(step)}
                        className="text-xs"
                      >
                        Pular
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => handleStepClick(step)}
                      className="text-xs bg-blue-600 hover:bg-blue-700"
                    >
                      Começar
                      <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Mensagem de conclusão */}
        {completed === steps.length && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-4">
            <div className="text-center mb-4">
              <p className="font-bold text-green-900 text-lg">
                🎉 Parabéns! Você completou o onboarding de evolução!
              </p>
              <p className="text-sm text-green-800 mt-1">
                Agora aproveite todas as funcionalidades do Ateliê Pro ao máximo!
              </p>
            </div>

            {/* Botões de ação */}
            <div className="flex gap-3 flex-col sm:flex-row justify-center">
              <Button
                onClick={handleFinalizeTour}
                disabled={isFinalizingOnboarding}
                className="flex-1 sm:flex-none bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold py-2.5 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
              >
                {isFinalizingOnboarding ? "Finalizando..." : "✓ Finalizar tour"}
              </Button>

              <a
                href="https://www.youtube.com/@ateliepro"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 sm:flex-none"
              >
                <Button
                  variant="outline"
                  className="w-full border-2 border-blue-400 text-blue-700 hover:bg-blue-50 font-semibold py-2.5 rounded-lg transition-all duration-200 hover:scale-105"
                >
                  <Youtube className="w-4 h-4 mr-2" />
                  Aprender mais
                </Button>
              </a>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
